import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { kDocChartSeries } from "../../../Admin/kReusableCoreTS/kDocChartSvgCore";
import { AppAssistChart, AppAssistChartSpec } from "./AppAssistChart";
import { AppIcons } from "./AppIcons";
import { AppMoney } from "./AppMoney";
import { AppMoneyEntry, AppMoneyFilter, AppMoneyGroup, AppMoneyModel, AppMoneyPerson, AppMoneyTotal } from "./AppMoneyModel";

// AppMoneyView - the Revenue view's markup (money ON). Strings only, every value escaped; no `style=` (CSP) - the
// month chart is the shared `kDocChartSvg` through `AppAssistChart` (classes, a `<title>` per bar, no inline style).
// ⛔ A field with no value is left out; nothing says "unknown".

/** Everything one paint needs. */
export class AppMoneyScreen {
    public All: AppMoneyEntry[] = [];
    public Filter: AppMoneyFilter = new AppMoneyFilter();
    public Today: string = "";
    /** A sentence under the toolbar (a refusal, "Saved."), or "". */
    public Note: string = "";
    /** New items (the add-row) need the service (`/money`). */
    public CanChange: boolean = false;
}

export class AppMoneyView {
    public static readonly RootId: string = "wac-money";
    public static readonly NoteId: string = "wac-money-note";
    public static readonly FilterAttr: string = "data-wac-mf";
    public static readonly ToolAttr: string = "data-wac-mtool";
    /** On the one table host whose grid carries the add-row. */
    public static readonly AddAttr: string = "data-wac-madd";
    /** The one empty-state sentence (items and per-contact views). */
    private static readonly Empty: string = "<p class=\"mempty\">No money matches these filters.</p>";
    /** The attribute naming a table host's group (`AppMoneyModel.Groups` key). */
    public static readonly GroupAttr: string = "data-wac-mgroup";

    public static Html = (S: AppMoneyScreen): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Shown: AppMoneyEntry[] = AppMoneyModel.Filtered(S.All, S.Filter);
            let Body: string = AppMoneyView.People(AppMoneyModel.People(Shown));
            if (S.Filter.View !== "people") { Body = AppMoneyView.Items(Shown, S.Filter, S.CanChange); }
            return "<section class=\"money\" id=\"" + AppMoneyView.RootId + "\" aria-label=\"Revenue\">"
                + AppMoneyView.Summary(S) + AppMoneyView.Toolbar(S) + AppMoneyView.ExcelButton(S, Shown) + AppMoneyView.Charts(S.All, S.Today)
                + "<p class=\"mnote\" id=\"" + AppMoneyView.NoteId + "\" role=\"status\">" + kHTMLCore.EscapeHtml(S.Note) + "</p>"
                + Body + "</section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.Html");
            return "";
        }
    };

    /**
     * "12,500 EUR" - ⭐ WHOLE UNITS, GROUPED THOUSANDS, NEVER DECIMALS (owner review 2026-11-01). DOCUMENTED EXCEPTION: pure
     * formatting; ERROR-SUPPRESSED-JUSTIFIED: falls back to the plain whole number.
     */
    public static Amount = (Minor: number, Currency: string): string => {
        try {
            return Math.round(AppMoney.Major(Minor, Currency)).toLocaleString("en-US", { maximumFractionDigits: 0 }) + " " + nz(Currency);
        } catch (e) {
            return AppMoneyModel.Plain(Minor, Currency) + " " + nz(Currency);
        }
    };

    /**
     * The currency a total box shows (owner review 2026-11-01: one line, not one per currency): among the currencies with
     * something open in that direction, the one with the most open items; a tie goes to the larger sum, then the code.
     * "" = nothing open. DOCUMENTED EXCEPTION: pure selection.
     */
    public static MainCurrency = (All: AppMoneyEntry[], In: boolean): string => {
        const Count: Record<string, number> = {};
        const Sum: Record<string, number> = {};
        let Best: string = "";
        for (const E of All) {
            if (E.State !== "open" || (E.Direction === "in") !== In) { continue; }
            Count[E.Currency] = (Count[E.Currency] || 0) + 1;
            Sum[E.Currency] = (Sum[E.Currency] || 0) + AppMoney.Major(E.Amount, E.Currency);
        }
        for (const C of Object.keys(Count).sort()) {
            if (Best === "" || Count[C] > Count[Best] || (Count[C] === Count[Best] && Sum[C] > Sum[Best])) { Best = C; }
        }
        return Best;
    };

    private static Summary = (S: AppMoneyScreen): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Open: AppMoneyTotal[] = AppMoneyModel.Totals(S.All);
            const Out: string[] = ["<div class=\"msummary\">"];
            Out.push(AppMoneyView.TotalBox("Owed to you", Open, true, AppMoneyView.MainCurrency(S.All, true)));
            Out.push(AppMoneyView.TotalBox("You owe", Open, false, AppMoneyView.MainCurrency(S.All, false)));
            const Done: AppMoneyTotal[] = AppMoneyModel.SettledThisMonth(S.All, S.Today);
            if (Done.length > 0) {
                const Words: string[] = [];
                for (const T of Done) { Words.push(AppMoneyView.Amount(T.In + T.Out, T.Currency)); }
                Out.push("<div class=\"mbox\"><h2>Settled this month</h2><p>" + kHTMLCore.EscapeHtml(Words.join(", ")) + "</p></div>");
            }
            Out.push("</div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.Summary");
            return "";
        }
    };

    /**
     * ONE total line in `Main` (owner review 2026-11-01: "no need" for a line per currency); the other currencies' totals are
     * the box's tooltip and a screen-reader sentence - never added together, never a visible list.
     */
    private static TotalBox = (Title: string, Open: AppMoneyTotal[], In: boolean, Main: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Line: string = "<li class=\"mnone\">Nothing open</li>";
            const Others: string[] = [];
            for (const T of Open) {
                let N: number = T.Out;
                if (In === true) { N = T.In; }
                if (N <= 0) { continue; }
                const Words: string = AppMoneyView.Amount(N, T.Currency);
                if (T.Currency === Main) { Line = "<li>" + kHTMLCore.EscapeHtml(Words) + "</li>"; } else { Others.push(Words); }
            }
            let Cls: string = "mbox out";
            if (In === true) { Cls = "mbox in"; }
            let Tip: string = "";
            let Also: string = "";
            if (Others.length > 0) {
                Tip = " title=\"" + kHTMLCore.EscapeAttr("Also open: " + Others.join(", ")) + "\"";
                Also = "<li class=\"sr\">" + kHTMLCore.EscapeHtml("Also open: " + Others.join(", ")) + "</li>";
            }
            return "<div class=\"" + Cls + "\"" + Tip + "><h2>" + Title + "</h2><ul>" + Line + Also + "</ul></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.TotalBox");
            return "";
        }
    };

    private static Toolbar = (S: AppMoneyScreen): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const F: AppMoneyFilter = S.Filter;
            const A: string = AppMoneyView.FilterAttr;
            const Cur: string[] = [""].concat(AppMoneyModel.Currencies(S.All));
            let Out: string = "<div class=\"mtools\" role=\"group\" aria-label=\"Filters\">"
                + AppMoneyView.Pick("view", "Show", [["items", "Items"], ["people", "By contact"]], F.View)
                + AppMoneyView.Pick("state", "State", [["open", "Open"], ["settled", "Settled"], ["all", "All"]], F.State)
                + AppMoneyView.Pick("direction", "Who owes", [["", "Both ways"], ["in", "Owed to you"], ["out", "You owe"]], F.Direction)
                + AppMoneyView.Pick("currency", "Currency", AppMoneyView.Pairs(Cur), F.Currency)
                + "<label class=\"mf\">From <input type=\"date\" autocomplete=\"off\" " + A + "=\"from\" value=\"" + kHTMLCore.EscapeAttr(F.From) + "\"></label>"
                + "<label class=\"mf\">To <input type=\"date\" autocomplete=\"off\" " + A + "=\"to\" value=\"" + kHTMLCore.EscapeAttr(F.To) + "\"></label>"
                + "<label class=\"mf msearch\"><span class=\"sr\">Search money</span><input type=\"search\" autocomplete=\"off\" " + A
                + "=\"search\" value=\"" + kHTMLCore.EscapeAttr(F.Search) + "\"></label></div>";
            // ⭐ OWNER 2026-11-02: no CSV button - each grid's own menu has "Export to Excel" (kEditableTable).
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.Toolbar");
            return "";
        }
    };

    /**
     * ⭐ OWNER 2026-11-02 ("One button for all"): ONE visible "Export to Excel" button - every table on the screen as a
     * sheet of one workbook (`AppMoneyTable.ExportAll`). Visible at every width (a phone has no right-click menu).
     * Only in the items view with something listed - the "By contact" view draws no tables.
     */
    public static ExcelButton = (S: AppMoneyScreen, Shown: AppMoneyEntry[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (S.Filter.View === "people" || Shown.length === 0) { return ""; }
            return "<div class=\"mexport\"><button type=\"button\" class=\"btn secondary sm\" " + AppMoneyView.ToolAttr + "=\"excel\">"
                + AppIcons.Svg(AppIcons.Download, 16) + "<span>Export to Excel</span></button></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.ExcelButton");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: pure mapping. */
    private static Pairs =(Codes: string[]): string[][] => {
        const Out: string[][] = [];
        for (const C of Codes) { if (C.length === 0) { Out.push(["", "All currencies"]); } else { Out.push([C, C]); } }
        return Out;
    };

    private static Pick = (Name: string, Label: string, Options: string[][], Now: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<label class=\"mf\">" + Label + " <select " + AppMoneyView.FilterAttr + "=\"" + Name + "\">"];
            for (const O of Options) {
                let Sel: string = "";
                if (O[0] === Now) { Sel = " selected"; }
                Out.push("<option value=\"" + kHTMLCore.EscapeAttr(O[0]) + "\"" + Sel + ">" + kHTMLCore.EscapeHtml(O[1]) + "</option>");
            }
            Out.push("</select></label>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.Pick");
            return "";
        }
    };

    /** One month chart per currency (the two with the most items), in/out per month, drawn by the shared renderer. */
    private static Charts = (All: AppMoneyEntry[], Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Months: string[] = AppMoneyModel.MonthKeys(Today);
            const Out: string[] = [];
            for (const C of AppMoneyModel.Currencies(All).slice(0, 2)) {
                const Data: number[][] = AppMoneyModel.Timeline(All, C, Months);
                const In: kDocChartSeries = new kDocChartSeries(); In.Name = "Owed to you"; In.Values = AppMoneyView.NoZero(Data[0]);
                const Ou: kDocChartSeries = new kDocChartSeries(); Ou.Name = "You owe"; Ou.Values = AppMoneyView.NoZero(Data[1]);
                const Title: string = AppMoneyView.ChartTitle(C);
                const Spec: AppAssistChartSpec | null = AppAssistChart.Read(AppAssistChart.SpecText("bar",
                    Title, Months.map(AppMoneyView.MonthLabel), [In, Ou], C));
                const Svg: string = AppAssistChart.Svg(Spec, "extremes");
                if (Svg.length > 0) {
                    Out.push("<figure class=\"mchart achart\"><figcaption class=\"mchartt\">" + kHTMLCore.EscapeHtml(Title)
                        + "</figcaption>" + Svg + "</figure>");
                }
            }
            if (Out.length === 0) { return ""; }
            return "<div class=\"mcharts\">" + Out.join("") + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.Charts");
            return "";
        }
    };

    /** "IDR per month" - the chart's visible caption and its `<title>`. DOCUMENTED EXCEPTION: pure formatting. */
    public static ChartTitle = (Currency: string): string => {
        return nz(Currency) + " per month";
    };

    /**
     * A month with nothing is "no value" (null), so the renderer draws no bar and no "0" label for it.
     * DOCUMENTED EXCEPTION: pure mapping, cannot throw on a number array.
     */
    public static NoZero = (Values: number[]): (number | null)[] => {
        const Out: (number | null)[] = [];
        for (const V of Values) {
            if (V === 0) { Out.push(null); } else { Out.push(V); }
        }
        return Out;
    };

    /** "2026-10" -> "Oct 26". DOCUMENTED EXCEPTION: pure formatting. */
    private static MonthLabel = (Key: string): string => {
        const Names: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const M: number = parseInt(Key.substring(5, 7), 10);
        if (isNaN(M) === true || M < 1 || M > 12) { return Key; }
        return Names[M - 1] + " " + Key.substring(2, 4);
    };

    /**
     * The item tables' hosts - `AppMoneyTable.Mount` fills each with the shared `kEditableTable` after the paint. The LAST
     * table carries the add-row (`AddAttr`, only when the service can add); with nothing to show, one empty table does.
     */
    private static Items = (Shown: AppMoneyEntry[], F: AppMoneyFilter, CanChange: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Groups: AppMoneyGroup[] = AppMoneyModel.Groups(Shown, F);
            const Out: string[] = [];
            if (Groups.length === 0) {
                Out.push(AppMoneyView.Empty);
                if (CanChange === true) { Out.push(AppMoneyView.Host("new", "New item", true)); }
                return Out.join("");
            }
            for (let I: number = 0; I < Groups.length; I++) {
                const G: AppMoneyGroup = Groups[I];
                Out.push("<section class=\"mgroup " + G.Key + "\" aria-label=\"" + kHTMLCore.EscapeAttr(G.Title) + "\"><h2>" + kHTMLCore.EscapeHtml(G.Title)
                    + " <span class=\"mcount\">" + String(G.Rows.length) + "</span></h2>"
                    + AppMoneyView.Host(G.Key, G.Title, CanChange === true && I === Groups.length - 1) + "</section>");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.Items");
            return "";
        }
    };

    /** One table host. DOCUMENTED EXCEPTION: pure markup from fixed keys. */
    private static Host = (Key: string, Title: string, Adds: boolean): string => {
        let Add: string = "";
        if (Adds === true) { Add = " " + AppMoneyView.AddAttr + "=\"1\""; }
        return "<div class=\"mtablewrap mtablehost\" " + AppMoneyView.GroupAttr + "=\"" + kHTMLCore.EscapeAttr(Key) + "\" aria-label=\""
            + kHTMLCore.EscapeAttr(Title) + "\"" + Add + "></div>";
    };

    private static People = (People: AppMoneyPerson[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (People.length === 0) { return AppMoneyView.Empty; }
            const Out: string[] = ["<div class=\"mtablewrap\"><table class=\"mtable mpeople\"><caption class=\"sr\">Money by contact</caption>"
                + "<thead><tr><th scope=\"col\">Contact</th><th scope=\"col\">Owed to you</th><th scope=\"col\">You owe</th>"
                + "<th scope=\"col\">Open items</th></tr></thead><tbody>"];
            for (const P of People) {
                const In: string[] = [];
                const Ou: string[] = [];
                for (const T of P.Totals) {
                    if (T.In > 0) { In.push(AppMoneyView.Amount(T.In, T.Currency)); }
                    if (T.Out > 0) { Ou.push(AppMoneyView.Amount(T.Out, T.Currency)); }
                }
                Out.push("<tr><td><a href=\"#c/" + kHTMLCore.EscapeAttr(encodeURIComponent(P.Href)) + "\">" + kHTMLCore.EscapeHtml(P.Contact)
                    + "</a></td><td>" + kHTMLCore.EscapeHtml(In.join(", ")) + "</td><td>" + kHTMLCore.EscapeHtml(Ou.join(", "))
                    + "</td><td>" + String(P.Open) + "</td></tr>");
            }
            Out.push("</tbody></table></div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyView.People");
            return "";
        }
    };
}
